<template>
  <v-card rounded elevation="8" v-if="ready">
    <v-card-title>
      <span class="mr-2">{{ $t('insights.headers.nextAvailableAppointmentReport') }}</span>
      <help-icon-tooltip>
        {{
          $t(
            'insights.messages.thisReportShortsTheNextAvailableAppointmentFromTodayForEachDockAndLoadType'
          )
        }}
        <br />
        <br />
        <i18n-t keypath="insights.messages.youCanToggleDockOrLoadTypeByClickingTheGroupHeader">
          <template #group>
            <b>group</b>
          </template>
        </i18n-t>
        <br />
        <br />
        {{
          $t(
            'insights.messages.thisIsANewExperimentalFeatureAndWeWillBeIteratingOnItForTheNextReleaseSoPleaseShareFeedbackWithTheOpendockCustomerSupportTeam'
          )
        }}
      </help-icon-tooltip>
    </v-card-title>
    <v-card-text class="pa-1">
      <v-data-table
        :items="loadTypeMetrics"
        :headers="headers"
        :group-by.sync="groupBy"
        :sort-by.sync="sortBy"
        hide-default-header
        hide-default-footer
        disable-pagination
        dense
        class="elevation-4">
        <template #thead="{ columns, headers }">
          <thead>
            <tr>
              <th :colspan="columns.length">
                <h3 class="my-3 text-right text--black">
                  {{ $t('insights.messages.groupedBy') }} {{ getGroupByLabel(groupByKey) }}
                  <br />
                  {{ $t('insights.messages.soonestAvailability') }}:
                  {{ getEarlyDate(loadTypeMetrics) }}
                </h3>
              </th>
            </tr>
            <tr>
              <template v-for="header in columns" :key="header.value">
                <th
                  v-if="header.title !== 'Group' && header.value !== groupByKey"
                  :colspan="columns.length - 1">
                  <span>{{ header.title }}</span>
                  <v-btn
                    @click.stop="setSortBy(header.value)"
                    density="compact"
                    variant="text"
                    icon="mdi-arrow-down"></v-btn>
                  <v-btn
                    @click.stop="setGroupBy(header.value)"
                    density="compact"
                    variant="text"
                    icon="mdi-format-list-group-plus"></v-btn>
                </th>
              </template>
            </tr>
          </thead>
        </template>
        <template #group-header="{ item, columns, isExpanded, toggleGroup, isGroupOpen }">
          <template
            :ref="
              el => {
                groupedHeaders[item.value] = { item, toggleGroup, isGroupOpen };
              }
            " />
          <tr>
            <th :colspan="columns.length - 1">
              <v-icon class="pb-1 pr-1" size="small" @click="toggleGroup(item)">
                {{ isGroupOpen(item) ? 'mdi-minus' : 'mdi-plus' }}
              </v-icon>
              <strong>{{ getGroupByLabel(item.key) }}</strong>
              : {{ item.value }}
            </th>
            <th>
              <span v-if="getGroupByLabel(item.key) !== 'Start'">
                Soonest: {{ getEarlyDate(item.items) }}
              </span>
            </th>
          </tr>
        </template>

        <template v-slot:item="{ item }">
          <tr>
            <td v-if="groupByKey !== 'dockName'" colspan="3">{{ item.dockName }}</td>
            <td v-if="groupByKey !== 'loadTypeName'" colspan="3">{{ item.loadTypeName }}</td>
            <td v-if="groupByKey !== 'start'">{{ item.start }}</td>
          </tr>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>

  <v-skeleton-loader v-else class="mx-auto" type="card"></v-skeleton-loader>
</template>

<script>
import { uniq } from 'lodash';
import moment from 'moment-timezone';

export default {
  data() {
    return {
      loadTypeMetrics: [],
      loadTypeNames: new Set(),
      ready: false,
      chartData: [],
      errorMsg: null,
      dataTest: '',
      groupBy: [{ key: 'dockName', order: 'asc' }],
      groupedHeaders: {},
      sortBy: [{ key: 'start', order: 'asc' }],
      shouldOpenAllHeaders: true
    };
  },
  computed: {
    headers() {
      return [
        { title: this.$t('global.specialtyTerms.docks'), value: 'dockName' },
        { title: this.$t('global.specialtyTerms.loadTypes'), value: 'loadTypeName' },
        { title: this.$t('insights.headers.start'), value: 'start' }
      ];
    },
    loadTypeIds() {
      return this.warehouseDocks.length > 0
        ? uniq(this.warehouseDocks.map(d => d.loadTypeIds).flat())
        : [];
    },
    warehouseDocks() {
      return this.selectedWarehouse?.docks ?? [];
    },
    groupByKey() {
      return this.groupBy[0].key;
    }
  },
  props: {
    selectedWarehouse: {
      type: Object,
      required: true,
      default: null
    }
  },
  watch: {
    selectedWarehouse() {
      this.getData();
    },
    groupedHeaders: {
      handler() {
        if (this.shouldOpenAllHeaders && Object.keys(this.groupedHeaders).length > 0) {
          Object.values(this.groupedHeaders).forEach(header => {
            if (!header.isGroupOpen(header.item)) {
              header.toggleGroup(header.item);
            }
          });
          this.shouldOpenAllHeaders = false;
        }
      },
      deep: true
    }
  },
  methods: {
    getEarlyDate(groupItems) {
      const minDate = Math.min(
        ...groupItems.map(i => {
          const original = i.raw?.startOriginal ?? i.startOriginal;
          return +this.formatDate(original, this.novaCore.DateTimeFormats.Date);
        })
      );

      return this.formatDate(String(minDate));
    },
    getGroupByLabel(groupKey) {
      return this.headers.find(h => h.value === groupKey).title;
    },
    formatDate(date, format = this.novaCore.DateTimeFormats.MonthDayYearSlashed) {
      const inputFormat = 'dddd, MMM D, YYYY @ h:mm A';
      // THe date is already on the warehouse timezone from the API
      return moment(date, inputFormat).locale(this.$i18n.locale).format(format);
    },
    formatData(res) {
      this.loadTypeMetrics = [];
      Object.values(res).forEach(loadTypeAvail => {
        this.loadTypeNames.add(loadTypeAvail.loadTypeName);
        Object.values(loadTypeAvail.firstAvailAppts).forEach(dockData => {
          this.loadTypeMetrics.push({
            dockName: dockData.dockName,
            loadTypeName: loadTypeAvail.loadTypeName,
            start: this.formatDate(dockData.startLocal),
            startOriginal: dockData.startLocal
          });
        });
      });

      this.loadTypeMetrics = this.loadTypeMetrics.sort(
        (a, b) =>
          this.formatDate(b.startOriginal, this.novaCore.DateTimeFormats.Date) -
          this.formatDate(a.startOriginal, this.novaCore.DateTimeFormats.Date)
      );
      this.shouldOpenAllHeaders = true;
    },
    async getData() {
      if (this.selectedWarehouse?.id && this.warehouseDocks.length > 0) {
        const response = await axios.post(`/metrics/loadtype/first-avail-appt`, {
          warehouseId: this.selectedWarehouse.id,
          loadtypeIds: this.loadTypeIds
        });

        if (response?.data) {
          this.formatData(response.data?.data ?? {});
          this.ready = true;
        }
      }
    },
    setSortBy(key) {
      this.sortBy = [{ key, order: this.sortBy[0].order === 'asc' ? 'desc' : 'asc' }];
    },
    setGroupBy(key) {
      this.shouldOpenAllHeaders = true;
      this.groupBy = [{ key }];
    }
  },
  mounted() {
    setInterval(() => {
      this.getData();
    }, 600000);

    this.getData();
  }
};
</script>
